/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import { memo, useMemo, useState } from 'react';

import { sortBy } from 'lodash';

import { Button, breakpoints } from '@coursera/cds-core';

import PartnerBox from 'bundles/partners-graphql/components/listing/cds/PartnerBox';
import type { PartnerListingPagePartnerFragment as PartnerListingPagePartner } from 'bundles/partners-graphql/queries/__generated__/GetAllPartners';

import _t from 'i18n!nls/partners-graphql';

const INITIAL_VISIBLE_COUNT = 21;
const SHOW_MORE_INCREMENT = 60;

type PropsToComponent = {
  partners: Array<PartnerListingPagePartner>;
  selectedCountry?: string;
  countryNamesMap?: Record<string, string>;
  searchQuery?: string;
  sortOrder?: '' | 'asc' | 'desc';
};

const styles = {
  grid: css`
    display: grid;
    grid-template-columns: repeat(7, 1fr);
    gap: var(--cds-spacing-200);
    margin: var(--cds-spacing-200) 0;

    ${breakpoints.down('lg')} {
      grid-template-columns: repeat(7, 1fr);
    }

    ${breakpoints.down('md')} {
      grid-template-columns: repeat(6, 1fr);
    }

    ${breakpoints.down('sm')} {
      grid-template-columns: repeat(2, 1fr);
    }

    ${breakpoints.down('xs')} {
      grid-template-columns: 1fr;
    }
  `,
  showMoreContainer: css`
    display: flex;
    justify-content: flex-start;
    margin: var(--cds-spacing-200) 0;
  `,
};

const PartnerBoxes = ({
  partners,
  selectedCountry,
  countryNamesMap = {},
  searchQuery = '',
  sortOrder = 'asc',
}: PropsToComponent) => {
  const [visibleCount, setVisibleCount] = useState(INITIAL_VISIBLE_COUNT);

  const sortedPartners = useMemo(() => {
    let filteredPartners = partners.filter(
      ({ slug, homePageOverride, squareLogo }) => !!squareLogo && (!!slug || !!homePageOverride)
    );

    if (selectedCountry) {
      filteredPartners = filteredPartners.filter(
        (partner) => partner?.location?.country?.toLowerCase() === selectedCountry.toLowerCase()
      );
    }

    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      filteredPartners = filteredPartners.filter((partner) => {
        if (partner?.name?.toLowerCase().includes(query)) return true;
        const countryCode = partner?.location?.country?.toUpperCase();
        if (countryCode && countryNamesMap[countryCode]) {
          return countryNamesMap[countryCode].toLowerCase().includes(query);
        }
        return false;
      });
    }

    const sorted = sortBy(filteredPartners, (partner) => partner?.name?.trim().toLowerCase());
    return sortOrder === 'desc' ? sorted.reverse() : sorted;
  }, [partners, selectedCountry, searchQuery, sortOrder, countryNamesMap]);

  const visiblePartners = sortedPartners.slice(0, visibleCount);
  const remainingCount = sortedPartners.length - visibleCount;
  const hasMore = remainingCount > 0;

  const handleShowMore = () => {
    setVisibleCount((prev) => prev + SHOW_MORE_INCREMENT);
  };

  return (
    <div>
      <div css={styles.grid} data-testid="partner-boxes">
        {visiblePartners.map((partner) => (
          <PartnerBox key={partner.id} partner={partner} countryNamesMap={countryNamesMap} />
        ))}
      </div>
      {hasMore && (
        <div css={styles.showMoreContainer}>
          <Button variant="secondary" size="small" onClick={handleShowMore}>
            {_t('Show #{count} more', { count: Math.min(remainingCount, SHOW_MORE_INCREMENT) })}
          </Button>
        </div>
      )}
    </div>
  );
};

export default memo(PartnerBoxes);
